/**
 * One-page overview of LMcad.
 *
 * Designed to fit on a single A4 / Letter page when printed (File · Print
 * · Save as PDF). Six content blocks separated by hairline rules:
 *   Hero · Problem+Solution · Impact · Market · Pricing+Contact · Sources
 *
 * Anything beyond six blocks pushes past one page. Competitive
 * positioning and roadmap live in the deck, not here.
 *
 * Pitched with India in the lead. Every stat carries a citation in
 * the Sources line at the bottom. Don't add a stat without a source.
 */
import { Brand } from '../components/atelier';
import { CALENDLY_URL, CONTACT_EMAIL } from '../lib/links';

export default function OnePager() {
  return (
    <div className="onepager surface" data-theme="light">
      <header className="op-head">
        <Brand />
        <div className="op-meta">
          <span>Overview</span>
          <span className="rule" />
          <span>One page</span>
          <span className="rule" />
          <span>2026</span>
        </div>
        <div className="op-head-contact">
          <span className="label">Contact</span>
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer">
            Book a demo
          </a>
        </div>
      </header>

      <section className="op-hero">
        <h1>
          A research-and-reasoning agent that gets you to{' '}
          <em>STEP, fast.</em>
        </h1>
        <p>
          Describe a part. LMcad asks what&rsquo;s missing, looks up the
          standards it doesn&rsquo;t have memorised, and hands back a
          parametric model with a clean STEP. A companion to CATIA, Creo,
          SolidWorks, and NX. Not a replacement.
        </p>
      </section>

      <hr className="op-rule" />

      <section className="op-grid two">
        <div>
          <div className="op-label">The problem</div>
          <p>
            First drafts are slow. A bracket concept costs an engineer
            half a morning in CATIA before any real design begins. India
            alone runs more than 1,700 Global Capability Centres
            employing close to 1.9&nbsp;million engineers, and a real
            chunk of every week is spent on this kind of throwaway
            drafting.
          </p>
        </div>
        <div>
          <div className="op-label">The solution</div>
          <p>
            One chat input. A parametric model with named dimensions, a
            3D preview, and a STEP file that round-trips into any CAD.
            Iterate by slider or re-prompt. Same toolchain, new entry
            point.
          </p>
        </div>
      </section>

      <hr className="op-rule" />

      <section>
        <div className="op-label">What gets saved</div>
        <div className="op-grid three">
          <div className="op-segment">
            <h3>Time</h3>
            <p>
              LMcad ships a first-draft part in 10&ndash;15&nbsp;minutes;
              a fully iterated, parameter-tuned part lands in
              ~30&nbsp;minutes. The same first draft costs an engineer
              half a morning in CATIA. McKinsey models gen-AI
              accelerating R&amp;D processes 20&ndash;80% across
              industries; Volkswagen + Autodesk took a wheel concept
              from 1.5 years to ~4 months.
            </p>
            <div className="op-segment-stat">
              <strong>30 min</strong>
              <span>LMcad full-iteration cycle, 2026</span>
            </div>
          </div>
          <div className="op-segment">
            <h3>Money</h3>
            <p>
              Gen-AI in R&amp;D is worth USD&nbsp;360&ndash;560&nbsp;B a
              year globally, ~10&ndash;15% of function cost (McKinsey).
              India already delivers 50&ndash;65% labour-cost savings
              vs.&nbsp;Europe on ER&amp;D (NASSCOM).
            </p>
            <div className="op-segment-stat">
              <strong>50&ndash;65%</strong>
              <span>India ER&amp;D cost arbitrage, NASSCOM 2024</span>
            </div>
          </div>
          <div className="op-segment">
            <h3>Efficiency</h3>
            <p>
              In an HBS &times; BCG randomised study of 758 consultants,
              AI users completed 12.2% more tasks, 25.1% faster, with
              40% higher quality on work inside the AI frontier. Goldman
              Sachs models +1.5&nbsp;pp/yr labour productivity for a
              decade.
            </p>
            <div className="op-segment-stat">
              <strong>25.1% faster</strong>
              <span>HBS &times; BCG field experiment, 2023</span>
            </div>
          </div>
        </div>
      </section>

      <hr className="op-rule" />

      <section>
        <div className="op-label">Where the market is</div>
        <div className="op-grid three">
          <div className="op-segment">
            <h3>B2B &middot; Tier&nbsp;1 &amp; Tier&nbsp;2</h3>
            <p>
              Bharat Forge, Sundaram Fasteners, Sona Comstar, Motherson,
              Endurance; OEMs like Tata, Mahindra, Maruti, Hero, Bajaj,
              TVS. Anchored to a USD&nbsp;80&nbsp;B auto-component
              sector growing 14% a year.
            </p>
            <div className="op-segment-stat">
              <strong>USD 80.2B</strong>
              <span>Auto-component sector, FY25</span>
            </div>
          </div>
          <div className="op-segment">
            <h3>B2C &middot; 3D printing</h3>
            <p>
              India&rsquo;s 3D-printing market hit USD&nbsp;707&nbsp;M
              in 2024, on a USD&nbsp;5&nbsp;B trajectory to 2034. WOL3D,
              3Ding, Bambu Lab India, and Imaginarium have made desktop
              printers mainstream.
            </p>
            <div className="op-segment-stat">
              <strong>USD 707M</strong>
              <span>India 3D printing, 2024</span>
            </div>
          </div>
          <div className="op-segment">
            <h3>Education</h3>
            <p>
              India runs 5,875 AICTE-approved engineering colleges in
              2025-26. Mechanical engineering takes 2.37&nbsp;lakh
              enrolments per year. NEP&nbsp;2020 leaves AI-CAD
              curriculum greenfield.
            </p>
            <div className="op-segment-stat">
              <strong>5,875</strong>
              <span>AICTE colleges, 2025-26</span>
            </div>
          </div>
        </div>
      </section>

      <hr className="op-rule" />

      <p className="op-cite">
        Sources: LMcad internal benchmark, 2026 &middot; McKinsey
        &ldquo;Economic potential of generative AI&rdquo; Jun 2023
        &middot; Goldman Sachs Briggs/Hatzius Mar 2023 &middot;
        Dell&rsquo;Acqua et al. HBS Working Paper 24-013 (BCG field
        experiment) Sep 2023 &middot; Autodesk + Volkswagen customer
        story &middot; ACMA FY25 Press Release &middot; Zinnov-NASSCOM
        India GCC Landscape 2024 &middot; NASSCOM Global ER&amp;D Shift
        2024 &middot; IMARC India 3D Printing Market 2024 &middot;
        AICTE 2025-26 Approval Process Handbook.
      </p>
    </div>
  );
}
